import { useCallback, useEffect, useState } from 'react';
import {
  Date,
  Link,
  Tab,
  TabContent,
  Tabs,
  Typography,
} from '@breeze-ai/ui-library';
import { Spinner } from '@breezeai-frontend/cargo-ui';
import invariant from 'tiny-invariant';

import { DISTRIBUTION_EMAIL } from '../../../app-constants';
import { useFeatureToggle, useUser } from '../../../context/auth/auth-hooks';
import { type BffFileModelListFileModel } from '../../../generated/api-client';
import { type Claim } from '../../../model/Claim';
import {
  getClaimById,
  getClaimFiles,
} from '../../../network/apis/claims/claims';
import { type ErrorResponse } from '../../../network/apis/types';
import { useRouteParams } from '../../../router/router-hooks';
import { useAppNavigation } from '../../../router/use-app-navigation';
import { reportException } from '../../../utils/error-reporting/error-reporting';
import { useTracking } from '../../../utils/snowplow/hooks';
import {
  type Asset,
  AssetsList,
} from '../../components/FileList/AssetsList/AssetsList';
import { mergeAssetsWithFiles } from '../../components/FileList/utils';
import { PageSection } from '../../components/PageSection/PageSection';
import { useSectionError } from '../../components/Section/SectionContext';
import { SectionLayout } from '../../components/Section/SectionLayout';
import { useDeleteClaimFile, useUploadClaimFile } from '../claimApiHooks';
import { ClaimContactDetails } from '../ClaimContactDetails/ClaimContactDetails';
import { ClaimCosts } from '../ClaimCosts/ClaimCosts';
import { ClaimEventDetails } from '../ClaimEventDetails/ClaimEventDetails';
import { ClaimStatusBadge } from '../ClaimStatusBadge/ClaimStatusBadge';
import styles from './ClaimDetailsPage.module.scss';

type ClaimRouteParams = {
  claimId: string;
};

export const ClaimDetailsPage = () => {
  useTracking();

  const { params } = useRouteParams<ClaimRouteParams>();
  const { navigateToSection } = useAppNavigation();
  const { setError } = useSectionError();
  const [loading, setLoading] = useState<boolean>();
  const [claim, setClaim] = useState<Claim>();
  const user = useUser();
  const isAuthorized = Boolean(user?.permissions.claims?.view);

  const isClaimDocumentsUploadEnabled = useFeatureToggle(
    'enable_claim_documents_upload',
  );

  const { claimId } = params;
  invariant(claimId, 'Claim ID is required');

  const [claimFiles, setClaimFiles] = useState<BffFileModelListFileModel[]>([]);
  const [claimAssets, setClaimAssets] = useState<Asset[]>([]);

  useEffect(() => {
    setClaimAssets((prev) => mergeAssetsWithFiles(prev, claimFiles));
  }, [claimFiles]);

  // TODO: Replace with react-query hooks
  const fetchClaimDetails = useCallback(
    async (claimId: string) => {
      try {
        const details = await getClaimById(claimId);
        const files = await getClaimFiles(claimId);
        setClaim(details);
        setClaimFiles(files);
      } catch (e) {
        reportException(e);
        setError(e as ErrorResponse);
      }
    },
    [setError],
  );

  // TODO: Replace with react-query hooks
  const fetchClaimFiles = useCallback(async (claimId: string) => {
    const files = await getClaimFiles(claimId);
    setClaimFiles(files);
  }, []);

  const retryFileUpload = (asset: Asset) => {
    setClaimAssets((prev) =>
      prev.map((a) =>
        a.name === asset.name
          ? {
              ...a,
              isUploading: true,
              isError: false,
            }
          : a,
      ),
    );

    if (!asset.content) {
      return;
    }

    uploadClaimFileMutation.mutate({
      claimId: parseInt(claimId),
      file: asset.content,
    });
  };

  const handleFileUploadError = (error: {
    fileName?: string;
    message: string;
    status: number;
    data: unknown;
  }) => {
    setClaimAssets((prev) =>
      prev.map((asset) =>
        asset.name === error?.fileName
          ? {
              ...asset,
              isUploading: false,
              isError: true,
              errorMessage: error.message,
              onTryAgain: () => retryFileUpload(asset),
            }
          : asset,
      ),
    );
  };

  const uploadClaimFileMutation = useUploadClaimFile({
    options: {
      onSuccess: () => {
        fetchClaimFiles(claimId);
      },
      onError: handleFileUploadError,
    },
  });

  const handleFileUpload = (files: FileList | null) => {
    if (files === null) {
      return;
    }

    const file = files[0];
    const asset: Asset = {
      name: file.name,
      isUploading: true,
      type: file.name.split('.').pop() ?? '',
      content: file,
    };

    setClaimAssets((prev) => [...prev, asset]);

    uploadClaimFileMutation.mutate({
      claimId: parseInt(claimId),
      file,
    });
  };

  const deleteClaimFileMutation = useDeleteClaimFile({
    options: {
      onSuccess: ({ fileId }) => {
        setClaimAssets((prev) => prev.filter((asset) => asset.id !== fileId));
        fetchClaimFiles(claimId);
      },
    },
  });

  useEffect(() => {
    if (!claim && claimId) {
      setLoading(true);
      fetchClaimDetails(claimId).finally(() => setLoading(false));
    }
  }, [claim, claimId, fetchClaimDetails]);

  return loading ? (
    <Spinner />
  ) : (
    // TODO - Convert to use new section layout
    <SectionLayout
      isAuthorized={isAuthorized}
      className={styles.claimsDetailsPage}
      contentClassName={styles.contentWrapper}
      testId="claim-details-page"
      title={
        <div className={styles.pageTitle} role="page-header">
          <Typography
            level="h2"
            prefixIcon="chevron-left"
            clickable
            onClick={() => navigateToSection('claims')}
          />
          <div className={styles.header}>
            <div className={styles.title}>
              {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
              <Typography level="h2">Claim {claim?.claim_number}</Typography>
              {claim && <ClaimStatusBadge claim={claim} />}
            </div>
            {claim && (
              // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
              <Typography block level="subtext" variant="input">
                Created on{' '}
                <Date
                  value={claim?.reported_time}
                  typographyProps={{ level: 'subtext' }}
                />
              </Typography>
            )}
          </div>
        </div>
      }
    >
      {claim && (
        <Tabs>
          <Tab id="claim-details" title="Claim Details" role="details-tab" />
          <Tab id="documents" title="Documents" role="documents-tab" />

          <TabContent tabId="claim-details">
            <div className={styles.gridLayout}>
              <div className={styles.main}>
                <ClaimEventDetails claim={claim} linkToPolicy={true} />
                <ClaimContactDetails claim={claim} />
              </div>
              <div className={styles.aside}>
                <ClaimCosts claim={claim} />
              </div>
            </div>
          </TabContent>
          <TabContent tabId="documents">
            <PageSection className={styles.documents}>
              <AssetsList
                // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
                title={<Typography level="h4">Claim files</Typography>}
                assets={[
                  ...claimAssets.map((asset) => ({
                    ...asset,
                    onDelete: () => {
                      if (!asset.id) return;
                      deleteClaimFileMutation.mutate({
                        fileId: asset.id,
                        claimId: parseInt(claimId),
                      });
                    },
                  })),
                ]}
                onFileUpload={
                  isClaimDocumentsUploadEnabled ? handleFileUpload : undefined
                }
              />
              {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
              <Typography block variant="input" className="pt-6">
                Want to share more files? Drop us an email{' '}
                <Link href={`mailto:${DISTRIBUTION_EMAIL}`} underline={false}>
                  {DISTRIBUTION_EMAIL}
                </Link>
              </Typography>
            </PageSection>
          </TabContent>
        </Tabs>
      )}
    </SectionLayout>
  );
};
